import React, { useState, useMemo, useEffect } from 'react';
import {
  Layers,
  Search,
  ChevronRight,
  BookOpen,
  CreditCard,
  Car,
  Award,
  FileText,
  Landmark,
  Globe,
  UserCheck,
  Briefcase,
  FolderPlus,
  ArrowRight,
  Sparkles,
  Filter,
  ArrowUpDown,
  CheckCircle2,
  ChevronLeft,
  X,
  Star,
  Download,
  DollarSign
} from 'lucide-react';
import { DocumentCategory, Product, Currency, SoftwareFormat } from '../types';
import { ProductCard } from './ProductCard';
import { ProductSkeletonGrid } from './ProductSkeleton';
import { getCategoryPublishedCount, isProductPublished } from '../lib/statsService';
import { INITIAL_CATEGORIES, searchCategories, CategoryModel, subscribeToCategoriesFromFirestore } from '../lib/categoriesService';
import { getCategorySlug } from '../lib/slugUtils';

interface CategoriesPageProps {
  darkMode: boolean;
  selectedCategory: DocumentCategory | 'All';
  onSelectCategory: (category: DocumentCategory | 'All') => void;
  onNavigateHome?: () => void;
  products: Product[];
  currency: Currency;
  wishlistItems: Product[];
  onToggleWishlist: (p: Product) => void;
  onAddToCart: (p: Product) => void;
  onQuickView: (p: Product) => void;
  onQuickBuy?: (p: Product) => void;
  isProductsLoading?: boolean;
  hasFirestoreLoaded?: boolean;
}

export const CategoriesPage: React.FC<CategoriesPageProps> = ({
  darkMode,
  selectedCategory,
  onSelectCategory,
  onNavigateHome,
  products = [],
  currency,
  wishlistItems,
  onToggleWishlist,
  onAddToCart,
  onQuickView,
  onQuickBuy,
  isProductsLoading = false,
  hasFirestoreLoaded = true,
}) => {
  // Category Search State
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [categoriesList, setCategoriesList] = useState<CategoryModel[]>(INITIAL_CATEGORIES);
  useEffect(() => {
    const unsub = subscribeToCategoriesFromFirestore((cats) => {
      setCategoriesList(cats.filter(c => c.status !== 'hidden').sort((a,b)=> (a.displayOrder||0) - (b.displayOrder||0)));
    });
    return () => unsub();
  }, []);

  // Listing Filters State
  const [selectedFormat, setSelectedFormat] = useState<SoftwareFormat | 'All'>('All');
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(500);
  const [minRating, setMinRating] = useState<number>(0);
  const [selectedLicense, setSelectedLicense] = useState<string>('All');
  
  // Smart Sort State
  const [sortBy, setSortBy] = useState<
    'newest' | 'oldest' | 'price-low' | 'price-high' | 'best-selling' | 'trending' | 'downloads' | 'rating'
  >('newest');

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 9;

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'BookOpen': return <BookOpen className="w-6 h-6" />;
      case 'CreditCard': return <CreditCard className="w-6 h-6" />;
      case 'Car': return <Car className="w-6 h-6" />;
      case 'Award': return <Award className="w-6 h-6" />;
      case 'FileText': return <FileText className="w-6 h-6" />;
      case 'Landmark': return <Landmark className="w-6 h-6" />;
      case 'Globe': return <Globe className="w-6 h-6" />;
      case 'UserCheck': return <UserCheck className="w-6 h-6" />;
      case 'Briefcase': return <Briefcase className="w-6 h-6" />;
      case 'Layers': return <Layers className="w-6 h-6 text-cyan-400" />;
      default: return <FolderPlus className="w-6 h-6" />;
    }
  };

  // Find currently selected Category details
  const currentCategoryInfo = useMemo(() => {
    if (selectedCategory === 'All') return null;
    return categoriesList.find(c => c.name.toLowerCase() === selectedCategory.toLowerCase()) || {
      id: `cat-${selectedCategory.toLowerCase()}`,
      name: selectedCategory,
      slug: getCategorySlug(selectedCategory),
      iconName: 'FolderPlus',
      gradient: 'from-blue-600 via-indigo-600 to-cyan-500',
      description: `High resolution vector and PSD digital templates for ${selectedCategory}.`,
      status: 'active' as const,
      displayOrder: 1
    };
  }, [selectedCategory]);

  // Filter Categories by Smart Category Search Query
  const searchedCategories = useMemo(() => {
    return searchCategories(categoriesList, categorySearchQuery);
  }, [categorySearchQuery]);

  // Filter products matching current category + smart filters
  const categoryProducts = useMemo(() => {
    const validProducts = (products || []).filter(p => p && isProductPublished(p));
    if (selectedCategory === 'All') return validProducts;

    return validProducts.filter((p) => {
      // 1. Category match
      if (p.category !== selectedCategory) return false;

      // 2. Format filter
      if (selectedFormat !== 'All') {
        if (!p.formats || !p.formats.includes(selectedFormat)) return false;
      }

      // 3. Price filter
      if (p.price < minPrice || p.price > maxPrice) return false;

      // 4. Rating filter
      if (minRating > 0 && p.rating < minRating) return false;

      // 5. License filter
      if (selectedLicense !== 'All' && (p as any).license !== selectedLicense) return false;

      return true;
    });
  }, [products, selectedCategory, selectedFormat, minPrice, maxPrice, minRating, selectedLicense]);

  // Sort filtered products
  const sortedProducts = useMemo(() => {
    const list = [...categoryProducts];
    switch (sortBy) {
      case 'newest':
        return list.sort((a, b) => {
          const tA1 = a.updatedAt ? new Date(a.updatedAt).getTime() : NaN;
          const tA2 = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
          const tB1 = b.updatedAt ? new Date(b.updatedAt).getTime() : NaN;
          const tB2 = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
          const vA = !isNaN(tA1) ? tA1 : (!isNaN(tA2) ? tA2 : 0);
          const vB = !isNaN(tB1) ? tB1 : (!isNaN(tB2) ? tB2 : 0);
          return vB - vA;
        });
      case 'oldest':
        return list.sort((a, b) => {
          const tA1 = a.updatedAt ? new Date(a.updatedAt).getTime() : NaN;
          const tA2 = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
          const tB1 = b.updatedAt ? new Date(b.updatedAt).getTime() : NaN;
          const tB2 = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
          const vA = !isNaN(tA1) ? tA1 : (!isNaN(tA2) ? tA2 : 0);
          const vB = !isNaN(tB1) ? tB1 : (!isNaN(tB2) ? tB2 : 0);
          return vA - vB;
        });
      case 'price-low':
        return list.sort((a, b) => a.price - b.price);
      case 'price-high':
        return list.sort((a, b) => b.price - a.price);
      case 'best-selling':
        return list.sort((a, b) => ((b.isPopular || (b as any).isBestSeller) ? 1 : 0) - ((a.isPopular || (a as any).isBestSeller) ? 1 : 0));
      case 'trending':
        return list.sort((a, b) => (b.isTrending ? 1 : 0) - (a.isTrending ? 1 : 0));
      case 'downloads':
        return list.sort((a, b) => (b.salesCount || (b as any).downloadsCount || 0) - (a.salesCount || (a as any).downloadsCount || 0));
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      default:
        return list;
    }
  }, [categoryProducts, sortBy]);

  // Paginated products
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage) || 1;
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedProducts.slice(start, start + itemsPerPage);
  }, [sortedProducts, currentPage, itemsPerPage]);

  const handleCategoryClick = (categoryName: DocumentCategory) => {
    onSelectCategory(categoryName);
    setCurrentPage(1);
  };

  return (
    <div className="py-10 relative z-10 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

        {/* BREADCRUMB HEADER */}
        <div className="flex items-center gap-2 text-xs font-bold text-slate-400 overflow-x-auto pb-1 min-w-0">
          <button 
            onClick={() => {
              if (onNavigateHome) {
                onNavigateHome();
              } else {
                onSelectCategory('All');
              }
            }} 
            className="hover:text-cyan-400 transition-colors flex items-center gap-1 cursor-pointer shrink-0"
          >
            Home
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
          <button 
            onClick={() => onSelectCategory('All')} 
            className="hover:text-cyan-400 transition-colors flex items-center gap-1 cursor-pointer shrink-0"
          >
            Categories
          </button>
          {selectedCategory !== 'All' && (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
              <span className="text-cyan-400 font-extrabold truncate max-w-[180px]">{selectedCategory}</span>
            </>
          )}
        </div>

        {/* HERO HEADER: Dynamic Header per Category or Catalog Overview */}
        {selectedCategory === 'All' ? (
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-extrabold uppercase tracking-widest shadow-sm">
              <Layers className="w-3.5 h-3.5" />
              <span>Enterprise Dynamic Categories</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
              Explore Vector <span className="text-gradient">Document Categories</span>
            </h1>

            <p className={`text-sm sm:text-base leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Select a category to view high-resolution 3D editable PSD, Illustrator AI, and vector document templates with live auto-counters.
            </p>
          </div>
        ) : (
          /* DYNAMIC CATEGORY HEADER */
          <div className={`p-6 sm:p-8 rounded-3xl border shadow-2xl relative overflow-hidden ${
            darkMode ? 'glass-card border-white/10' : 'glass-card-light border-slate-200'
          }`}>
            <div className={`absolute -right-20 -top-20 w-64 h-64 rounded-full bg-gradient-to-br ${currentCategoryInfo?.gradient || 'from-blue-600 to-cyan-500'} opacity-15 blur-3xl pointer-events-none`} />

            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10 w-full">
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-tr ${currentCategoryInfo?.gradient || 'from-blue-600 to-cyan-500'} p-3 sm:p-4 flex items-center justify-center text-white shadow-xl shrink-0`}>
                  {getIcon(currentCategoryInfo?.iconName || 'BookOpen')}
                </div>

                <div className="space-y-1 mt-0.5 sm:mt-0">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-extrabold text-[11px] uppercase tracking-wider border border-cyan-500/30">
                      Category Listing
                    </span>
                  </div>

                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white flex items-center gap-3">
                    <span>{selectedCategory}</span>
                    <span className="text-sm font-bold text-cyan-400 bg-slate-900/95 px-3 py-1 rounded-full border border-cyan-500/20">
                      ({getCategoryPublishedCount(products, selectedCategory)})
                    </span>
                  </h1>

                  <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
                    {currentCategoryInfo?.description}
                  </p>
                </div>
              </div>

              <button
                onClick={() => onSelectCategory('All')}
                className="px-4 py-2.5 rounded-xl bg-slate-900/95 hover:bg-slate-800 border border-slate-700 text-xs font-bold text-cyan-400 hover:text-white transition-all flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                <span>View All Categories</span>
              </button>
            </div>
          </div>
        )}

        {/* SMART CATEGORY SEARCH BOX */}
        <div className={`p-4 sm:p-5 rounded-3xl border shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 ${
          darkMode ? 'glass-card border-white/10' : 'glass-card-light border-slate-200'
        }`}>
          <div className="relative w-full md:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
            <input
              type="text"
              value={categorySearchQuery}
              onChange={(e) => setCategorySearchQuery(e.target.value)}
              placeholder="Search categories (e.g. Passport, ID Card, Bank, Visa)..."
              className={`w-full pl-11 pr-4 py-2.5 rounded-2xl border text-xs font-bold transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${
                darkMode
                  ? 'bg-slate-900/90 border-slate-700 text-white placeholder-slate-400'
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
              }`}
            />
            {categorySearchQuery && (
              <button
                onClick={() => setCategorySearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto w-full pb-2 md:pb-0 scrollbar-none min-w-0">
            <button
              onClick={() => onSelectCategory('All')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all whitespace-nowrap shrink-0 ${
                selectedCategory === 'All'
                  ? 'bg-blue-600 text-white border-blue-400 shadow-md shadow-blue-500/20'
                  : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
              }`}
            >
              All Categories ({categoriesList.length})
            </button>
            {categoriesList.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.name as DocumentCategory)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all whitespace-nowrap shrink-0 ${
                  selectedCategory === cat.name
                    ? 'bg-cyan-600 text-white border-cyan-400 shadow-md'
                    : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
                }`}
              >
                {cat.name} ({getCategoryPublishedCount(products, cat.name)})
              </button>
            ))}
          </div>
        </div>

        {/* CATEGORY CARDS GRID (When browsing all categories or searching categories) */}
        {selectedCategory === 'All' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span>Select Document Category</span>
                <span className="text-xs text-slate-400 font-normal">({searchedCategories.length} categories)</span>
              </h2>
            </div>

            {searchedCategories.length === 0 ? (
              <div className="p-8 text-center rounded-2xl bg-slate-900/50 border border-slate-800 text-slate-400 text-xs">
                No matching category found for "<span className="text-white font-bold">{categorySearchQuery}</span>". Try searching for "Passport", "ID Card", or "Bank Statement".
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {searchedCategories.map((cat) => {
                  const count = getCategoryPublishedCount(products, cat.name);
                  const isSelected = selectedCategory === cat.name;

                  return (
                    <div
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.name as DocumentCategory)}
                      className={`group relative p-5 rounded-3xl border transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden md:hover:-translate-y-1.5 ${
                        isSelected
                          ? 'border-cyan-400 bg-cyan-950/40 shadow-xl shadow-cyan-500/20'
                          : darkMode
                          ? 'glass-card border-white/10 hover:border-cyan-500/40 hover:bg-slate-900/90'
                          : 'glass-card-light border-slate-200 hover:border-blue-400'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${cat.gradient || 'from-blue-600 to-indigo-600'} p-3 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                            {getIcon(cat.iconName)}
                          </div>
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-slate-900/90 border border-slate-700 text-cyan-400">
                            {cat.name} ({count})
                          </span>
                        </div>

                        <div>
                          <h3 className="text-base font-extrabold group-hover:text-cyan-400 transition-colors text-white">
                            {cat.name}
                          </h3>
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                            {cat.description}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs font-bold text-cyan-400 group-hover:text-cyan-300">
                        <span>Browse {cat.name}</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SMART FILTERS & SORT TOOLBAR (When Category is Selected) */}
        {selectedCategory !== 'All' && (
          <div className="space-y-6">
            
            {/* Toolbar */}
            <div className={`p-4 rounded-2xl border flex flex-wrap items-center justify-between gap-4 ${
              darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-slate-100 border-slate-200'
            }`}>
              
              {/* Left Filters */}
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                
                {/* Format Selector */}
                <div className="flex items-center gap-1.5 bg-slate-950/95 border border-slate-800 px-3 py-1.5 rounded-xl">
                  <span className="text-slate-400">Format:</span>
                  <select
                    value={selectedFormat}
                    onChange={(e) => { setSelectedFormat(e.target.value as any); setCurrentPage(1); }}
                    className="bg-transparent text-white font-bold focus:outline-none cursor-pointer"
                  >
                    <option value="All">All Formats</option>
                    <option value="PSD">Photoshop (.PSD)</option>
                    <option value="AI">Illustrator (.AI)</option>
                    <option value="CDR">CorelDRAW (.CDR)</option>
                  </select>
                </div>

                {/* Price Range */}
                <div className="flex items-center gap-1.5 bg-slate-950/95 border border-slate-800 px-3 py-1.5 rounded-xl">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-slate-400">Max Price:</span>
                  <span className="text-white font-bold">${maxPrice}</span>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={maxPrice}
                    onChange={(e) => { setMaxPrice(Number(e.target.value)); setCurrentPage(1); }}
                    className="w-20 accent-cyan-400 cursor-pointer"
                  />
                </div>

                {/* Min Rating */}
                <div className="flex items-center gap-1.5 bg-slate-950/95 border border-slate-800 px-3 py-1.5 rounded-xl">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  <select
                    value={minRating}
                    onChange={(e) => { setMinRating(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-transparent text-white font-bold focus:outline-none cursor-pointer"
                  >
                    <option value={0}>All Ratings</option>
                    <option value={4.5}>4.5★ & Above</option>
                    <option value={4.8}>4.8★ & Above</option>
                  </select>
                </div>

              </div>

              {/* Right Smart Sort */}
              <div className="flex items-center gap-2 text-xs font-semibold">
                <ArrowUpDown className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-slate-400">Sort By:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="bg-slate-950 border border-slate-800 text-white font-bold px-3 py-1.5 rounded-xl focus:outline-none cursor-pointer"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="price-low">Price: Low → High</option>
                  <option value="price-high">Price: High → Low</option>
                  <option value="best-selling">Best Selling</option>
                  <option value="trending">Trending</option>
                  <option value="downloads">Most Downloaded</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>

            </div>

            {/* PRODUCT GRID OR SKELETON OR EMPTY STATE */}
            {isProductsLoading ? (
              <ProductSkeletonGrid count={6} darkMode={darkMode} />
            ) : paginatedProducts.length === 0 ? ( !hasFirestoreLoaded ? ( <ProductSkeletonGrid count={6} darkMode={darkMode} /> ) : ( 
              /* EMPTY CATEGORY STATE requirement */
              <div className="p-12 text-center rounded-3xl glass-card border border-white/10 space-y-4 max-w-xl mx-auto my-8">
                <div className="w-16 h-16 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center mx-auto text-slate-500">
                  <FolderPlus className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-extrabold text-white">No products available in this category.</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  There are no active products matching your filter criteria under <span className="text-cyan-400 font-bold">{selectedCategory}</span> right now. Try adjusting your format or price filters.
                </p>
                <button
                  onClick={() => {
                    setSelectedFormat('All');
                    setMaxPrice(500);
                    setMinRating(0);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md"
                >
                  Reset Filters
                </button>
              </div>
            ) ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedProducts.map((p, idx) => (
                  <ProductCard
                    key={`${p.id}-${idx}`}
                    product={p}
                    currency={currency}
                    isInWishlist={wishlistItems.some(i => i.id === p.id)}
                    onToggleWishlist={onToggleWishlist}
                    onAddToCart={onAddToCart}
                    onQuickView={onQuickView}
                    onQuickBuy={onQuickBuy}
                    darkMode={darkMode}
                  />
                ))}
              </div>
            )}

            {/* PAGINATION NUMBERS */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-6 border-t border-slate-800">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white disabled:opacity-40 hover:bg-slate-800 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }).map((_, idx) => {
                  const pageNum = idx + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-xl text-xs font-black transition-all border ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-cyan-400 shadow-lg shadow-blue-500/20'
                          : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white disabled:opacity-40 hover:bg-slate-800 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};
